/**
 * F3 — Admin / users / :id full profile page.
 *
 * One-page view of everything an admin wants about a sales user:
 *   • Header: avatar, name, email, role, designation, office, status
 *   • KPI tiles: active clients, MTD collected, conversion, incentive earned
 *   • Three-tab body: Clients (scoped mini-ledger) · Incentives · Activity
 *   • "Edit profile" button → modal for salesDesignation / officeBranch / designation
 *
 * Pulls a single rich payload from GET /api/users/:id/profile. Sales side
 * doesn't have access — this whole page sits behind PrivateAdmin.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Mail, Phone, MapPin, Edit3, Wallet, TrendingUp, CheckCircle2,
    Trophy, Activity as ActivityIcon, ClipboardList, IndianRupee, Users as UsersIcon,
    LogIn,
} from 'lucide-react';
import TeamLeads from '../sales/TeamLeads';
import api from '../../utils/api';
import PerformanceKpiTiles from '../../components/PerformanceKpiTiles';
import ProfileEditor from '../../components/ProfileEditor';
import { describeActivity } from '../../utils/activityDescription';
import { useAuth } from '../../store/auth';

const inr = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
        .format(Number(v || 0));
const fd  = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STAGE_CHIP = {
    token:     'bg-amber-100 text-amber-800',
    loi:       'bg-sky-100 text-sky-800',
    agreement: 'bg-violet-100 text-violet-800',
    paid:      'bg-emerald-100 text-emerald-800',
    refunded:  'bg-rose-100 text-rose-800',
};
const STATUS_CHIP_USER = {
    active:    'bg-emerald-100 text-emerald-800',
    pending:   'bg-amber-100 text-amber-800',
    suspended: 'bg-rose-100 text-rose-800',
    rejected:  'bg-rose-100 text-rose-800',
};

export default function UserProfile() {
    const { id } = useParams();
    const nav = useNavigate();
    const startImpersonation = useAuth(s => s.startImpersonation);
    const [data, setData]         = useState(null);
    const [tab, setTab]           = useState('profile');
    const [impersonating, setImpersonating] = useState(false);
    const [directory, setDirectory] = useState({ designations: [], offices: [] });

    const load = () => api.get(`/users/${id}/profile`).then(r => setData(r.data));
    const loadDir = () => api.get('/org-directory')
        .then(r => setDirectory({
            designations: (r.data.grouped?.designation || []).filter(d => d.active),
            offices:      (r.data.grouped?.office      || []).filter(o => o.active),
        }))
        .catch(() => { /* not fatal — fall back to hardcoded list */ });
    useEffect(() => { load(); loadDir(); }, [id]);

    const handleImpersonate = async () => {
        if (!window.confirm(`Login as ${data.user.name}?\n\nYou'll see the app exactly as they do. A banner will appear so you can exit and return to your admin session.`)) return;
        try {
            setImpersonating(true);
            await startImpersonation(id);
            // Redirect to that user's home (sales or admin)
            const u = data.user;
            const dest = (u.role === 'admin' || ['sub_admin', 'manager', 'finance', 'operations', 'performance_marketing'].includes(u.role))
                ? '/admin/dashboard'
                : '/sales/dashboard';
            window.location.href = dest;
        } catch (e) {
            alert(e?.response?.data?.error || 'Impersonation failed.');
            setImpersonating(false);
        }
    };

    if (!data) return <div className="ag-card p-10 text-center text-sm text-ink-500">Loading…</div>;

    const u = data.user;
    const k = data.kpis;

    return (
        <div className="space-y-4 max-w-6xl">
            <Link to="/admin/users" className="text-xs text-ink-500 hover:text-brand-teal-700 inline-flex items-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to users
            </Link>

            {/* Header */}
            <div className="ag-card p-5 flex flex-col md:flex-row gap-5 items-start">
                <div className="h-16 w-16 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center text-xl font-extrabold uppercase shrink-0">
                    {u.name?.split(/\s+/).map(w => w[0]).slice(0, 2).join('') || '?'}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                        <h1 className="text-2xl font-extrabold">{u.name}</h1>
                        <span className={`ag-chip ${STATUS_CHIP_USER[u.status] || 'bg-ink-100 text-ink-700'}`}>{u.status}</span>
                        {u.role && <span className="ag-chip bg-ink-100 text-ink-700">{u.role}</span>}
                        {u.salesDesignation && <span className="ag-chip bg-brand-teal-50 text-brand-teal-700">{u.salesDesignation.toUpperCase()}</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm text-ink-700">
                        <div className="inline-flex items-center gap-1.5"><Mail   className="h-3.5 w-3.5 text-ink-500" /> {u.email}</div>
                        <div className="inline-flex items-center gap-1.5"><Phone  className="h-3.5 w-3.5 text-ink-500" /> {u.phone || '—'}</div>
                        <div className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-ink-500" /> {u.officeBranch || u.territory || '—'}</div>
                    </div>
                    {u.designation && (
                        <div className="text-xs text-ink-500 mt-2">Designation: <b className="text-ink-700">{u.designation}</b></div>
                    )}
                </div>

                {/* Impersonate button */}
                {u.status === 'active' && (
                    <button
                        onClick={handleImpersonate}
                        disabled={impersonating}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                                   bg-violet-600 hover:bg-violet-700 active:bg-violet-800
                                   text-white shadow-sm transition disabled:opacity-60 shrink-0"
                        title={`Login as ${u.name} and see the app from their perspective`}
                    >
                        <LogIn className="h-4 w-4" />
                        {impersonating ? 'Switching…' : `Login as ${u.name.split(' ')[0]}`}
                    </button>
                )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi icon={ClipboardList} label="Total clients"      value={k.clientsCount} />
                <Kpi icon={CheckCircle2}  label="Active clients"     value={k.activeClients} />
                <Kpi icon={Wallet}        label="MTD collected"      value={inr(k.mtdCollected)} />
                <Kpi icon={TrendingUp}    label="Conversion"         value={`${k.conversion}%`} />
                <Kpi icon={Trophy}        label="Incentive earned"   value={inr(k.incentiveEarned)} sub="locked + paid · net of clawback" />
            </div>

            {/* Tabs */}
            <div className="flex gap-2 flex-wrap">
                <Tab id="profile"     label="Profile"     icon={Edit3}         tab={tab} setTab={setTab} />
                <Tab id="performance" label="Performance" icon={TrendingUp}    tab={tab} setTab={setTab} />
                <Tab id="clients"     label="Clients"     icon={ClipboardList} count={data.clients.length}    tab={tab} setTab={setTab} />
                <Tab id="leads"       label="Zoho leads"  icon={UsersIcon}                                       tab={tab} setTab={setTab} />
                <Tab id="incentives"  label="Incentives"  icon={Trophy}        count={data.incentives.length} tab={tab} setTab={setTab} />
                <Tab id="activity"    label="Activity"    icon={ActivityIcon}  count={data.activity.length}   tab={tab} setTab={setTab} />
            </div>

            {tab === 'profile'     && (
                <ProfileEditor
                    user={u}
                    directory={directory}
                    mode="admin"
                    onSaved={() => { load(); loadDir(); }}
                />
            )}
            {tab === 'performance' && <PerformanceTab user={u} />}
            {tab === 'clients'     && <ClientsTab    rows={data.clients} />}
            {tab === 'leads'       && <TeamLeads viewAs={u._id} embedded />}
            {tab === 'incentives'  && <IncentivesTab rows={data.incentives} />}
            {tab === 'activity'    && <ActivityTab   rows={data.activity} />}

        </div>
    );
}

/* ─────────── Performance tab — embedded KPI tiles scoped to this user ─────────── */
function PerformanceTab({ user }) {
    /* Future: switch scope per role (BDM → team rollup). For now uses
       the personal scope. */
    return (
        <div className="space-y-3">
            <div className="ag-card p-4 text-xs text-ink-500">
                KPI tiles below reflect <b className="text-ink-700">{user.name}'s</b> personal funnel for the
                current month. For the full org-wide view use <Link to="/admin/kpi-analytics" className="ag-link">KPI Analytics</Link>.
            </div>
            <PerformanceKpiTiles scope="personal" embedded showFunnel hideActions />
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub }) {
    return (
        <div className="ag-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold inline-flex items-center gap-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-xl font-extrabold mt-0.5 tabular-nums truncate">{value}</div>
            {sub && <div className="text-[10px] text-ink-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function Tab({ id, label, icon: Icon, count, tab, setTab }) {
    return (
        <button onClick={() => setTab(id)} className={`ag-tab ${tab === id ? 'ag-tab-active' : 'ag-tab-inactive'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
            <span className="text-[10px] ml-1 opacity-70">{count}</span>
        </button>
    );
}

/* ─────────── Clients tab (mini ledger scoped to this user) ─────────── */
function ClientsTab({ rows }) {
    if (rows.length === 0) {
        return <div className="ag-card p-8 text-center text-sm text-ink-500">No clients linked to this user yet.</div>;
    }
    return (
        <div className="ag-card p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-slate-900 text-slate-300">
                    <tr>
                        <Th>Submitted</Th><Th>Client</Th><Th>Stage</Th>
                        <Th align="right">Deal</Th>
                        <Th align="right">Collected</Th>
                        <Th align="right">Balance</Th>
                        <Th align="right">%</Th>
                        <Th>Status</Th>
                        <th></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                    {rows.map(r => {
                        const stage = r.currentStage || 'token';
                        return (
                            <tr key={r._id} className="odd:bg-white even:bg-slate-50/60 hover:bg-brand-teal/5">
                                <Td className="text-xs text-ink-500">{fd(r.createdAt)}</Td>
                                <Td>
                                    <div className="font-medium">{r.data?.name || '—'}</div>
                                    <div className="text-[11px] text-ink-500 font-mono tabular-nums">{r.data?.phone || ''}</div>
                                </Td>
                                <Td>
                                    <span className={`ag-chip ${STAGE_CHIP[stage] || 'bg-ink-100 text-ink-700'}`}>{stage.toUpperCase()}</span>
                                </Td>
                                <Td align="right" mono>{inr(r.data?.dealAmount || 0)}</Td>
                                <Td align="right" mono className="text-emerald-700">{inr(r.totalReceived || 0)}</Td>
                                <Td align="right" mono>{inr(r.balanceAmount || 0)}</Td>
                                <Td align="right" mono>{Number(r.receivedPercent || 0).toFixed(1)}%</Td>
                                <Td>
                                    <span className="text-[11px] uppercase tracking-wider">{r.clientStatus || 'active'}</span>
                                </Td>
                                <Td align="right">
                                    <Link to={`/admin/clients/${r._id}`} className="text-xs text-brand-teal-700 hover:underline">View →</Link>
                                </Td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────── Incentives tab ─────────── */
function IncentivesTab({ rows }) {
    if (rows.length === 0) {
        return <div className="ag-card p-8 text-center text-sm text-ink-500">No incentive involvement yet.</div>;
    }
    return (
        <div className="ag-card p-0 overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-900 text-slate-300">
                    <tr>
                        <Th>Client</Th><Th>Status</Th><Th>Role</Th>
                        <Th align="right">Gross share</Th>
                        <Th align="right">Clawback</Th>
                        <Th align="right">Net</Th>
                        <Th>Created</Th>
                        <th></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                    {rows.map(r => (
                        <tr key={r._id}>
                            <Td className="font-medium">{r.clientName || '—'}</Td>
                            <Td><span className="text-[11px] uppercase tracking-wider text-ink-500">{r.status}</span></Td>
                            <Td><span className="text-[11px] uppercase tracking-wider text-ink-500">{r.myRole || '—'}</span></Td>
                            <Td align="right" mono>{inr(r.myGross || 0)}</Td>
                            <Td align="right" mono className={r.myClawback > 0 ? 'text-rose-700' : 'text-ink-400'}>
                                {r.myClawback > 0 ? `− ${inr(r.myClawback)}` : '—'}
                            </Td>
                            <Td align="right" mono className="font-semibold text-emerald-700">{inr(r.myNet || 0)}</Td>
                            <Td className="text-xs text-ink-500">{fd(r.createdAt)}</Td>
                            <Td align="right">
                                <Link to={`/admin/incentives/closures/${r._id}`} className="text-xs text-brand-teal-700 hover:underline">Open →</Link>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────── Activity tab ─────────── */
function ActivityTab({ rows }) {
    if (rows.length === 0) {
        return <div className="ag-card p-8 text-center text-sm text-ink-500">No recent activity.</div>;
    }
    return (
        <div className="ag-card p-0 overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-900 text-slate-300">
                    <tr>
                        <Th>When</Th><Th>What happened</Th><Th>Status</Th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                    {rows.map((a, i) => {
                        const { summary, detail } = describeActivity(a);
                        return (
                            <tr key={a._id || i}>
                                <Td className="text-xs text-ink-500 tabular-nums whitespace-nowrap">{new Date(a.at).toLocaleString('en-IN')}</Td>
                                <Td>
                                    <div className="text-sm text-ink-900">{summary}</div>
                                    {detail && <div className="text-[10px] font-mono text-ink-400 truncate max-w-[320px]">{detail}</div>}
                                </Td>
                                <Td className="text-xs">{a.status || '—'}</Td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────── Th / Td primitives ─────────── */
function Th({ children, align = 'left' }) {
    return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`}>{children}</th>;
}
function Td({ children, align = 'left', mono = false, className = '' }) {
    return <td className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : ''} ${mono ? 'tabular-nums' : ''} ${className}`}>{children}</td>;
}

